import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from '../../Librarian/librarianSwal';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import { useAdminLibrary } from '../AdminLibraryContext';
import '../../Librarian/Reports/ReportDashboard.css';
import '../../Librarian/lib-page-header.css';

import {
  getBooksByLibrary,
  getBookCopiesByLibrary,
  getReservationsByLibrary,
  getIssuesByLibrary,
  getFinesByLibrary,
  getMembersByLibrary,
  getErrorMessage
} from '../../api/api';

const EMPTY = {
  books: 0,
  copies: 0,
  availableCopies: 0,
  issuedCopies: 0,
  reservations: 0,
  pendingRequests: 0,
  activeIssues: 0,
  members: 0,
  fines: 0,
  unpaidFines: 0
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { libraries, loading: librariesLoading, setSelectedLibraryId } = useAdminLibrary();

  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading || librariesLoading);
  const [totals, setTotals] = useState(EMPTY);
  const [perLibrary, setPerLibrary] = useState([]);

  const statsForLibrary = async (libraryId) => {
    const [books, copies, reservations, issues, fines, members] = await Promise.all([
      getBooksByLibrary(libraryId),
      getBookCopiesByLibrary(libraryId),
      getReservationsByLibrary(libraryId),
      getIssuesByLibrary(libraryId),
      getFinesByLibrary(libraryId),
      getMembersByLibrary(libraryId)
    ]);

    return {
      books: books?.length || 0,
      copies: copies?.length || 0,
      availableCopies: copies?.filter((c) => c.availability_status === 'available').length || 0,
      issuedCopies: copies?.filter((c) => c.availability_status === 'issued').length || 0,
      reservations: reservations?.length || 0,
      pendingRequests: reservations?.filter((r) => r.status === 'requested').length || 0,
      activeIssues: issues?.filter((i) => i.issue_status === 'issued').length || 0,
      members: members?.length || 0,
      fines: fines?.length || 0,
      unpaidFines: fines?.filter((f) => (f.fine_status || f.status) === 'unpaid').length || 0
    };
  };

  const fetchAll = async () => {
    if (!libraries || libraries.length === 0) {
      setTotals(EMPTY);
      setPerLibrary([]);
      return;
    }

    try {
      setLoading(true);

      const rows = await Promise.all(
        libraries.map(async (lib) => {
          try {
            const s = await statsForLibrary(lib.id);
            return { library: lib, stats: s, error: false };
          } catch (e) {
            return { library: lib, stats: { ...EMPTY }, error: true };
          }
        })
      );

      const sum = rows.reduce((acc, r) => {
        Object.keys(EMPTY).forEach((k) => {
          acc[k] += r.stats[k] || 0;
        });
        return acc;
      }, { ...EMPTY });

      setTotals(sum);
      setPerLibrary(rows);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraries]);

  const openLibrary = (libraryId, path) => {
    setSelectedLibraryId(libraryId);
    navigate(path);
  };

  const Card = ({ title, value, accent, icon }) => (
    <div className="report-card" style={{ '--accent': accent }}>
      {icon && (
        <span
          className="report-card__icon"
          style={{ position: 'absolute', top: 15, right: 16, fontSize: 20, color: 'var(--accent)', opacity: 0.9 }}
        >
          <i className={icon} />
        </span>
      )}
      <span className="report-card__title">{title}</span>
      <span className="report-card__value">{value}</span>
    </div>
  );

  const libName = (lib) => lib.library_name || lib.name || `Library #${lib.id}`;

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-tachometer-alt" /></div>
          <div>
            <h3 className="lib-page-header__title">Admin Dashboard</h3>
            <div className="lib-page-header__subtitle">
              Overview across all {libraries?.length || 0} librar{(libraries?.length || 0) === 1 ? 'y' : 'ies'}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .admin-lib-table-wrap { margin-top: 22px; background: #fff; border: 1px solid #e6ecf5;
          border-radius: 14px; overflow: hidden; box-shadow: 0 6px 18px rgba(31,45,80,.06); }
        .admin-lib-table-head { display:flex; align-items:center; justify-content:space-between;
          padding: 16px 20px; border-bottom: 1px solid #eef2f9; }
        .admin-lib-table-head h4 { margin:0; font-size: 15.5px; font-weight: 800; color:#1f2d50; }
        .admin-lib-table-head span { font-size: 12.5px; color:#6b7a99; }
        .admin-lib-table-scroll { overflow-x:auto; }
        .admin-lib-table { width:100%; border-collapse: collapse; min-width: 760px; }
        .admin-lib-table th, .admin-lib-table td { padding: 12px 16px; text-align:left; font-size: 13px; white-space: nowrap; }
        .admin-lib-table thead th { background:#f6f8fc; color:#5b6b86; font-weight:700;
          text-transform:uppercase; letter-spacing:.03em; font-size: 11.5px; border-bottom:1px solid #eef2f9; }
        .admin-lib-table tbody tr { border-bottom: 1px solid #f2f5fa; transition: background .15s ease; }
        .admin-lib-table tbody tr:hover { background:#f9fbff; cursor:pointer; }
        .admin-lib-table td.num { text-align:right; font-variant-numeric: tabular-nums; font-weight:600; color:#2c3a5c; }
        .admin-lib-name { font-weight:700; color:#1d4ed8; }
        .admin-lib-badge { display:inline-block; padding:2px 8px; border-radius:999px; font-size:11px;
          font-weight:700; background:#fee2e2; color:#b91c1c; }
      `}</style>

      {showLoader ? (
        <Loader />
      ) : (libraries?.length || 0) === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7a99' }}>
          No libraries found.
        </div>
      ) : (
        <>
          <div className="report-grid">
            <Card title="Libraries" value={libraries.length} accent="#6366f1" icon="fas fa-school" />
            <Card title="Books" value={totals.books} accent="#6f8fe6" icon="fas fa-book" />
            <Card title="Total Copies" value={totals.copies} accent="#7c5cfc" icon="fas fa-layer-group" />
            <Card title="Available Copies" value={totals.availableCopies} accent="#22b07d" icon="fas fa-check-circle" />
            <Card title="Issued Copies" value={totals.issuedCopies} accent="#f5a524" icon="fas fa-book-reader" />
            <Card title="Active Issues" value={totals.activeIssues} accent="#0ea5e9" icon="fas fa-exchange-alt" />
            <Card title="Parent Requests" value={totals.reservations} accent="#d6669a" icon="fas fa-inbox" />
            <Card title="Pending Requests" value={totals.pendingRequests} accent="#f59e0b" icon="fas fa-hourglass-half" />
            <Card title="Members" value={totals.members} accent="#2dabb5" icon="fas fa-users" />
            <Card title="Fines" value={totals.fines} accent="#ef5350" icon="fas fa-coins" />
            <Card title="Unpaid Fines" value={totals.unpaidFines} accent="#f97362" icon="fas fa-exclamation-triangle" />
          </div>

          <div className="admin-lib-table-wrap">
            <div className="admin-lib-table-head">
              <h4>Per-library breakdown</h4>
              <span>Click a row to open that library's book list</span>
            </div>
            <div className="admin-lib-table-scroll">
              <table className="admin-lib-table">
                <thead>
                  <tr>
                    <th>Library</th>
                    <th style={{ textAlign: 'right' }}>Books</th>
                    <th style={{ textAlign: 'right' }}>Copies</th>
                    <th style={{ textAlign: 'right' }}>Available</th>
                    <th style={{ textAlign: 'right' }}>Issued</th>
                    <th style={{ textAlign: 'right' }}>Members</th>
                    <th style={{ textAlign: 'right' }}>Pending Req.</th>
                    <th style={{ textAlign: 'right' }}>Unpaid Fines</th>
                  </tr>
                </thead>
                <tbody>
                  {perLibrary.map(({ library, stats, error }) => (
                    <tr key={library.id} onClick={() => openLibrary(library.id, '/admin/books')}>
                      <td>
                        <span className="admin-lib-name">{libName(library)}</span>
                        {error && <span className="admin-lib-badge" style={{ marginLeft: 8 }}>load failed</span>}
                      </td>
                      <td className="num">{stats.books}</td>
                      <td className="num">{stats.copies}</td>
                      <td className="num">{stats.availableCopies}</td>
                      <td className="num">{stats.issuedCopies}</td>
                      <td className="num">{stats.members}</td>
                      <td className="num">{stats.pendingRequests}</td>
                      <td className="num">{stats.unpaidFines}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="quick-actions">
            <div className="quick-actions__head">
              <h4>Quick Actions</h4>
              <span>Opens the currently selected library</span>
            </div>

            <div className="quick-actions__grid">
              <button className="qa-btn" style={{ '--qa': '#6f8fe6' }} onClick={() => navigate('/admin/books')}>
                <span className="qa-btn__icon">📚</span>
                Book List
              </button>
              <button className="qa-btn" style={{ '--qa': '#2dabb5' }} onClick={() => navigate('/admin/members')}>
                <span className="qa-btn__icon">👥</span>
                Members
              </button>
              <button className="qa-btn" style={{ '--qa': '#0ea5e9' }} onClick={() => navigate('/admin/issues')}>
                <span className="qa-btn__icon">↪</span>
                Issued Books
              </button>
              <button className="qa-btn" style={{ '--qa': '#ef5350' }} onClick={() => navigate('/admin/fines')}>
                <span className="qa-btn__icon">₹</span>
                Fines
              </button>
              <button className="qa-btn" style={{ '--qa': '#f59e0b' }} onClick={() => navigate('/admin/announcements')}>
                <span className="qa-btn__icon">📢</span>
                Announcements
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
